import type { IApiConfig } from '@app/state/config/interfaces'

export const OAUTH_WINDOW_NAME = 'oauth'

export interface OAuthSuccess {
  code: string
}

export function startOAuth(apiConfig: IApiConfig): Promise<OAuthSuccess> {
  const pollIntervalMs = 500
  const windowSettings = getWindowSettings(window, apiConfig)

  return new Promise<OAuthSuccess>((resolve, reject) => {
    const authWindow = window.open(
      windowSettings.path,
      OAUTH_WINDOW_NAME,
      windowSettings.options,
    )

    const pollInterval = setInterval(() => {
      if (isWindowClosed(authWindow)) {
        clearInterval(pollInterval)
        reject(new Error('window_closed'))
        return
      }

      interrogateWindowForCode(authWindow).then((result) => {
        if (result.complete) {
          clearInterval(pollInterval)
          authWindow.close()
          if (result.status === 'success' && !!result.code) {
            resolve({ code: result.code })
          } else if (result.status === 'error') {
            reject(new Error(result.message))
          }
        }
      })
    }, pollIntervalMs)
  })
}

type WindowCodeCheckResult =
  | { complete: false }
  | { complete: true; status: 'error'; message: string }
  | { complete: true; status: 'success'; code: string }

function interrogateWindowForCode(
  authWindow: Window,
): Promise<WindowCodeCheckResult> {
  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      resolve({ complete: false })
      window.removeEventListener('message', receiveHandler)
    }, 250)

    function receiveHandler(event: MessageEvent) {
      if (event.data.type === 'location-response') {
        clearTimeout(timeoutId)
        window.removeEventListener('message', receiveHandler)

        const parsedQuery = parseQueryString(event.data.locationHref)

        if (parsedQuery.code) {
          resolve({ complete: true, status: 'success', code: parsedQuery.code })
        } else if (parsedQuery.error) {
          resolve({
            complete: true,
            status: 'error',
            message: parsedQuery.error,
          })
        } else {
          resolve({ complete: false })
        }
      }
    }

    window.addEventListener('message', receiveHandler)
    authWindow.postMessage({ type: 'location-request' }, '*')
  })
}

function isWindowClosed(authWindow: Window): boolean {
  return (
    authWindow === undefined ||
    authWindow.closed ||
    authWindow.closed === undefined
  )
}

function parseQueryString(url: string): { [key: string]: string } {
  const queryBeginIndex = url.indexOf('?')
  if (queryBeginIndex >= 0) {
    return url
      .slice(queryBeginIndex + 1)
      .split('&')
      .reduce((accum, pair) => {
        const parts = pair.split('=')
        accum[parts[0]] = decodeURI(parts[1])
        return accum
      }, {} as { [key: string]: string })
  } else {
    return {}
  }
}

export function makeLandingPath(apiConfig: IApiConfig): string {
  const params = [
    `${apiConfig.auth.oAuthAuthorizeUrl}?prompt=login`,
    'response_type=code',
    `client_id=${apiConfig.auth.oAuthClientId}`,
    `redirect_uri=${apiConfig.auth.oAuthRedirectUrl}`,
    `scope=${apiConfig.auth.oAuthScope}`,
  ]

  if (apiConfig.auth.oAuthResource && apiConfig.auth.oAuthResource !== "") {
    params.push(`resource=${apiConfig.auth.oAuthResource}`)
  }

  return params.join('&')
}

export interface OAuthWindowSettings {
  path: string
  width: number
  height: number
  left: number
  top: number
  options: string
}

export function getWindowSettings(
  originWindow: Window,
  apiConfig: IApiConfig,
): OAuthWindowSettings {
  const width = 500
  const height = 560
  const centeredPos = getCenteredPosition(originWindow, width, height)
  const options = [
    `width=${width}`,
    `height=${height}`,
    `top=${centeredPos.top}`,
    `left=${centeredPos.left}`,
    `resizable=0`,
  ].join(',')

  return {
    path: makeLandingPath(apiConfig),
    width,
    height,
    ...centeredPos,
    options,
  }
}

function getCenteredPosition(
  originWindow: Window,
  targetWidth: number,
  targetHeight: number,
): { top: number; left: number } {
  const targetDeltaX = (originWindow.outerWidth - targetWidth) / 2
  const targetDeltaY = (originWindow.outerHeight - targetHeight) / 2

  return {
    left: originWindow.screenX + targetDeltaX,
    top: originWindow.screenY + targetDeltaY,
  }
}
